import Link from "next/link";
import { aiToolLabel } from "@cw/forms/ai-tools";
import type { BundlesData } from "@/lib/cms";
import { ModuleIcon } from "../ModuleIcon";
import { MODULE_ICONS, formatCount, migrationLabel, type Environment } from "./intake-options";

type ConfigurationSummaryProps = {
  data: BundlesData;
  selected: BundlesData["bundles"][number] | undefined;
  environment: Environment;
};

const SECTION_LABEL = "text-[11px] font-semibold uppercase tracking-[0.14em] text-slate";
const PENDING = "Not yet provided";

const NEXT_STEPS = [
  "An Edge specialist reviews your configuration.",
  "We confirm the operating model and any migration scope with you.",
  "You receive an onboarding plan sized to your environment.",
];

/** Sticky companion that restates the configuration as it is built. Reflects state; owns none. */
export function ConfigurationSummary({ data, selected, environment }: ConfigurationSummaryProps) {
  const modules = data.rows.filter((row) => selected?.includes.includes(row.slug));
  // Required counts always show a placeholder; optional systems appear only once entered.
  const optionalSystems: [string, string][] = ([
    ["Platform", environment.currentPlatform.trim()],
    ["CRM", environment.crm.trim()],
    ["ERP", environment.erp.trim()],
  ] as [string, string][]).filter(([, value]) => value);
  const profile: [string, string][] = [
    ["Users", formatCount(environment.totalUsers) || PENDING],
    ["Locations", formatCount(environment.totalSites) || PENDING],
    ...optionalSystems,
    ["Migration", migrationLabel(environment.planningToMigrate)],
    ["AI tools", environment.aiTools.length ? environment.aiTools.map(aiToolLabel).join(", ") : "None selected"],
  ];

  return (
    <aside aria-labelledby="edge-summary-title" aria-live="polite" className="rounded-[10px] border border-hairline bg-paper-raised lg:sticky lg:top-24">
      <div className="border-b border-hairline p-6">
        <p className={SECTION_LABEL}>Your Edge</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <h2 id="edge-summary-title" className="font-display text-xl font-semibold tracking-tight text-ink">{selected?.name ?? "Choose a bundle"}</h2>
          {selected?.highlight ? <span className="rounded-sm bg-signal-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-signal-ink">Recommended</span> : null}
        </div>
        {selected ? <p className="mt-1.5 text-sm leading-6 text-slate">{selected.tagline}</p> : null}
      </div>

      <section aria-labelledby="edge-summary-included" className="border-b border-hairline p-6">
        <h3 id="edge-summary-included" className={SECTION_LABEL}>Included with your plan</h3>
        <ul className="mt-4 space-y-3">
          {modules.map((row) => (
            <li key={row.slug} className="flex items-center gap-3 text-sm font-medium text-ink">
              <span aria-hidden="true" className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-soft text-ink"><ModuleIcon name={MODULE_ICONS[row.slug] ?? row.slug} className="size-4" /></span>
              {row.label}
            </li>
          ))}
          {modules.length === 0 ? <li className="text-sm text-slate">Select a tier to see what is included.</li> : null}
        </ul>
      </section>

      <section aria-labelledby="edge-summary-profile" className="border-b border-hairline p-6">
        <h3 id="edge-summary-profile" className={SECTION_LABEL}>Organization profile</h3>
        <dl className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
          {profile.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-slate">{label}</dt>
              <dd className={`min-w-0 break-words text-right ${value === PENDING ? "text-slate/70" : "font-medium text-ink"}`}>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="edge-summary-migration" className="border-b border-hairline p-6">
        <h3 id="edge-summary-migration" className={SECTION_LABEL}>Migration benefit</h3>
        <p className="mt-3 text-sm leading-6 text-ink">One hosted voice migration included every 24 months.</p>
        <p className="mt-1 text-xs leading-5 text-slate">Terms and conditions apply.</p>
        <Link href="/migration-services/" className="mt-3 inline-flex min-h-8 items-center text-xs font-medium text-slate underline underline-offset-4 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink">Explore Migration Services</Link>
      </section>

      <section aria-labelledby="edge-summary-next" className="p-6">
        <h3 id="edge-summary-next" className={SECTION_LABEL}>What happens next</h3>
        <ol className="mt-4 space-y-3">
          {NEXT_STEPS.map((step, index) => (
            <li key={step} className="flex gap-3 text-sm leading-6 text-slate-strong">
              <span aria-hidden="true" className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-hairline text-[11px] font-semibold text-ink">{index + 1}</span>
              {step}
            </li>
          ))}
        </ol>
      </section>
    </aside>
  );
}
